/**
 * Inquiry Store
 *
 * Manages inquiry cart state for both bulk orders and sample requests
 * Task: T5.2.2 - Bulk Order cart
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  productId: string;
  productName: string;
  productSlug: string;
  productImage: string | null;
  quantity: number;
  color: string | null;
  notes: string;
  type: 'SAMPLE' | 'BULK';
}

interface InquiryState {
  // Cart items
  items: CartItem[];

  // Actions
  addItem: (item: Omit<CartItem, 'notes'> & { notes?: string }) => void;
  updateItem: (productId: string, type: 'SAMPLE' | 'BULK', updates: Partial<CartItem>) => void;
  removeItem: (productId: string, type: 'SAMPLE' | 'BULK') => void;
  clearCart: () => void;
  clearItemsByType: (type: 'SAMPLE' | 'BULK') => void;
  getItemCount: () => number;
}

/**
 * Important Issue #7 Fix: Normalize color for comparison
 * Treats undefined, null, and empty string as equivalent
 */
function normalizeColor(color: string | null | undefined): string | null {
  const trimmed = color?.trim();
  return trimmed || null;
}

export const useInquiryStore = create<InquiryState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) =>
        set((state) => {
          // Important Issue #7 Fix: Normalize colors before comparison
          const newColor = normalizeColor(item.color);

          const existingIndex = state.items.findIndex((i) => {
            const existingColor = normalizeColor(i.color);
            return (
              i.productId === item.productId &&
              existingColor === newColor &&
              i.type === item.type
            );
          });

          if (existingIndex >= 0) {
            // Update existing item quantity (immutably)
            const updatedItems = [...state.items];
            updatedItems[existingIndex] = {
              ...updatedItems[existingIndex],
              quantity: updatedItems[existingIndex].quantity + item.quantity,
            };
            return { items: updatedItems };
          }

          // Add new item with normalized color
          return {
            items: [
              ...state.items,
              {
                ...item,
                color: newColor,
                notes: item.notes || '',
              },
            ],
          };
        }),

      updateItem: (productId, type, updates) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId && item.type === type
              ? { ...item, ...updates }
              : item
          ),
        })),

      removeItem: (productId, type) =>
        set((state) => ({
          items: state.items.filter(
            (item) => !(item.productId === productId && item.type === type)
          ),
        })),

      clearCart: () => set({ items: [] }),

      clearItemsByType: (type) =>
        set((state) => ({
          items: state.items.filter((item) => item.type !== type),
        })),

      getItemCount: () => get().items.length,
    }),
    {
      name: 'gifting-inquiry-cart',
      version: 1,
      migrate: (persisted: unknown, version: number) => {
        if (version === 0) {
          // Migration: old items don't have a type field — default to BULK
          const state = persisted as { items?: Array<Record<string, unknown>> };
          if (state?.items) {
            state.items = state.items.map((item) => ({
              ...item,
              type: item.type || 'BULK',
            }));
          }
        }
        return persisted as InquiryState;
      },
    }
  )
);
